import { useLayoutEffect, useRef, useState } from 'react';
import type { DefaultFeedTabKey } from '@/core/models/preferences';
import { readFeedVisit, saveFeedVisit } from './feedMemory';

export function useFeedMemory(
  owner: string,
  enabled: boolean,
  defaultTab: DefaultFeedTabKey,
  ready: boolean,
) {
  const [tab, setTab] = useRememberedFeedTab(owner, enabled, defaultTab);
  useFeedPosition(owner, enabled, ready, tab);
  return [tab, setTab] as const;
}
export function useRememberedFeedTab(
  owner: string,
  enabled: boolean,
  defaultTab: DefaultFeedTabKey,
) {
  const [state, setState] = useState(() => ({
    owner,
    tab: enabled ? (readFeedVisit(owner)?.tab ?? defaultTab) : defaultTab,
  }));
  if (state.owner !== owner)
    setState({ owner, tab: enabled ? (readFeedVisit(owner)?.tab ?? defaultTab) : defaultTab });
  return [state.tab, (tab: DefaultFeedTabKey) => setState({ owner, tab })] as const;
}
export function useFeedPosition(
  owner: string,
  enabled: boolean,
  ready: boolean,
  tab: DefaultFeedTabKey,
) {
  const latestTab = useRef(tab);
  useLayoutEffect(() => {
    latestTab.current = tab;
  }, [tab]);
  useLayoutEffect(() => {
    if (!enabled) {
      saveFeedVisit(owner, null);
      return;
    }
    if (!ready) return;
    const center = document.querySelector<HTMLElement>('[data-testid="feed-center"]');
    const mobile = window.matchMedia('(max-width: 767.98px)').matches;
    const scroller = mobile ? document.scrollingElement : center;
    if (!scroller) return;
    const visit = readFeedVisit(owner);
    const top = () => (mobile ? 56 : (center?.getBoundingClientRect().top ?? 0) + 52);
    if (visit) {
      scroller.scrollTop = visit.offset;
      const anchor = [...document.querySelectorAll<HTMLElement>('[data-post-id]')].find(
        (el) => el.dataset.postId === visit.anchor,
      );
      if (anchor && Number.isFinite(visit.delta))
        scroller.scrollTop += anchor.getBoundingClientRect().top - top() - visit.delta!;
    }
    function save() {
      const anchor = [...document.querySelectorAll<HTMLElement>('[data-post-id]')].find(
        (el) => el.getBoundingClientRect().bottom > top(),
      );
      saveFeedVisit(owner, {
        tab: latestTab.current,
        offset: scroller!.scrollTop,
        anchor: anchor?.dataset.postId,
        delta: anchor ? anchor.getBoundingClientRect().top - top() : undefined,
      });
    }
    const target = mobile ? window : scroller;
    target.addEventListener('scroll', save, { passive: true });
    window.addEventListener('pagehide', save);
    return () => {
      save();
      target.removeEventListener('scroll', save);
      window.removeEventListener('pagehide', save);
    };
  }, [owner, enabled, ready]);
}
